// <!-- GRACE:MODULE_START name="ui_auth" -->
// <!-- GRACE:DEPENDENCY target="ui_auth" -->
// <LLM_NOTE>
// При монтировании AuthProvider: проверяет наличие токена в localStorage и вызывает
// getCurrentUser() для его валидации. Если запрос падает (401 или сеть недоступна) —
// токен удаляется. Это означает: при недоступности бэкенда пользователь разлогинивается.
// </LLM_NOTE>
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
  AuthUser,
  login as apiLogin,
  register as apiRegister,
  getCurrentUser,
  changePassword as apiChangePassword,
  logout as apiLogout,
  getToken,
  removeToken,
} from '../services/authService';

interface AuthContextType {
  user: AuthUser | null;
  isAuthenticated: boolean;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, fullName?: string) => Promise<string>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<string>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// <!-- GRACE:CONTRACT function="AuthProvider" -->
export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  // Check token on mount
  useEffect(() => {
    const token = getToken();
    if (!token) {
      setLoading(false);
      return;
    }
    getCurrentUser()
      .then(setUser)
      .catch(() => removeToken())
      .finally(() => setLoading(false));
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const data = await apiLogin(email, password);
    setUser(data.user);
  }, []);

  const register = useCallback(async (email: string, password: string, fullName?: string) => {
    const data = await apiRegister(email, password, fullName);
    return data.message;
  }, []);

  const changePassword = useCallback(async (currentPassword: string, newPassword: string) => {
    const data = await apiChangePassword(currentPassword, newPassword);
    return data.message;
  }, []);

  const logout = useCallback(() => {
    apiLogout();
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        loading,
        login,
        register,
        changePassword,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

// <!-- GRACE:CONTRACT function="useAuth" -->
export function useAuth(): AuthContextType {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

// <!-- GRACE:MODULE_END name="ui_auth" -->
